// src/components/CommunityStats.js
// Onglet "Chiffres" de la Communauté — version 2 : ambiance claire (eau + sable),
// centrée sur 3 modules "plongée" : l'échelle des brevets, les générations,
// et le profil des plongées (loisir/déco + gaz). Données de GET /stats.
import React from "react";

const MAIN_LEVELS = ["open_water", "advanced_open_water", "rescue", "divemaster", "instructor"];
const SPECIALTIES = ["nitrox", "advanced_nitrox", "deep", "ccr", "trimix_normo", "trimix_hypo", "cavern", "intro_cave", "full_cave"];

// Dégradé de profondeur pour l'échelle des brevets (surface → fond).
const DEPTH_COLORS = ["#5cc4ea", "#2ea4d6", "#1785b8", "#0a6494", "#04476e"];
// Rampe d'âge : une seule teinte chaude qui fonce avec l'âge (ordonné → a du sens).
const AGE_COLORS = ["#ffd98a", "#f4b24a", "#ef8f4f", "#e5703c", "#c9542b"];

const AGE_ORDER = [
    ["u25", "< 25"],
    ["25_34", "25–34"],
    ["35_44", "35–44"],
    ["45_54", "45–54"],
    ["55p", "55+"],
];

export default function CommunityStats({ stats, t, onPickCert }) {
    const certCounts = stats?.certCounts || {};
    const ageBrackets = stats?.ageBrackets || {};
    const diveTypeCounts = stats?.diveTypeCounts || {};
    const gasCounts = stats?.gasCounts || {};
    const visibilityCounts = stats?.visibilityCounts || {};

    // --- Brevets ---
    const mainRows = MAIN_LEVELS.map((k) => ({ key: k, count: certCounts[k] || 0 }));
    const mainMax = Math.max(...mainRows.map((r) => r.count), 1);
    const specRows = SPECIALTIES.map((k) => ({ key: k, count: certCounts[k] || 0 }))
        .filter((r) => r.count > 0)
        .sort((a, b) => b.count - a.count);
    const specMax = Math.max(...specRows.map((r) => r.count), 1);
    const levelsHasData = mainRows.some((r) => r.count > 0);

    // --- Âge ---
    const ageRows = AGE_ORDER.map(([k, label]) => ({ label, count: ageBrackets[k] || 0 }));
    const ageMax = Math.max(...ageRows.map((r) => r.count), 1);
    const ageHasData = ageRows.some((r) => r.count > 0);

    // Pyramide des âges par sexe (oldest en haut). Anonyme, agrégé.
    const ageGenderCounts = stats?.ageGenderCounts || {};
    const pyramidRows = AGE_ORDER.map(([k, label]) => ({
        label,
        male: ageGenderCounts[k]?.male || 0,
        female: ageGenderCounts[k]?.female || 0,
    })).reverse();
    const pyramidMax = Math.max(1, ...pyramidRows.flatMap((r) => [r.male, r.female]));
    const pyramidHasData = pyramidRows.some((r) => r.male > 0 || r.female > 0);

    // --- Profil : type de plongée + gaz ---
    const typeSegments = [
        { label: t("diveTypes.loisir"), count: diveTypeCounts.loisir || 0, color: "#2a9d8f" },
        { label: t("diveTypes.deco"), count: diveTypeCounts.deco || 0, color: "#ef6f53" },
        { label: t("diveTypes.egal"), count: diveTypeCounts.egal || 0, color: "#c3cdd4" },
    ].filter((s) => s.count > 0);
    const gasSegments = [
        { label: t("gasTypes.air"), count: gasCounts.air || 0, color: "#7fc9e8" },
        { label: t("gasTypes.nx"), count: gasCounts.nx || 0, color: "#2a9d8f" },
        { label: t("gasTypes.tx"), count: gasCounts.tx || 0, color: "#7b6cd0" },
        { label: t("gasTypes.egal"), count: gasCounts.egal || 0, color: "#c3cdd4" },
    ].filter((s) => s.count > 0);
    const visibilitySegments = [
        { label: t("communityStats.visibility.public"), count: visibilityCounts.public || 0, color: "#2a9d8f" },
        { label: t("communityStats.visibility.friends"), count: visibilityCounts.friends || 0, color: "#e9a23b" },
        { label: t("communityStats.visibility.club"), count: visibilityCounts.club || 0, color: "#5a9bd4" },
        { label: t("communityStats.visibility.private"), count: visibilityCounts.private || 0, color: "#c05b6d" },
    ].filter((s) => s.count > 0);
    const profileHasData = typeSegments.length > 0 || gasSegments.length > 0 || visibilitySegments.length > 0;

    return (
        <div className="aq2" role="region" aria-label={t("community.tabs.stats")}>
            <div className="aq2-bubbles" aria-hidden="true">
                {[...Array(8)].map((_, i) => <span key={i} className={`aq2-bubble b${i % 4}`} />)}
            </div>

            <div className="aq2-content">

                {/* ---- L'échelle des brevets (5 niveaux, dégradé de profondeur) ---- */}
                <section className="aq2-card">
                    <h3 className="aq2-title">{t("communityStats.certTitle")}</h3>
                    {levelsHasData ? (
                        <div className="aq2-gauge">
                            {mainRows.map((r, i) => (
                                <button
                                    type="button"
                                    key={r.key}
                                    className="aq2-level"
                                    onClick={() => onPickCert && onPickCert(r.key)}
                                    title={t("communityStats.seeDivers")}
                                >
                                    <span className="aq2-level-name">{t(`certifications.${r.key}`)}</span>
                                    <div className="aq2-level-track">
                                        <div className="aq2-level-fill" style={{ width: `${(r.count / mainMax) * 100}%`, background: DEPTH_COLORS[i] }} />
                                    </div>
                                    <span className="aq2-level-count">{r.count}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <p className="aq2-empty">{t("communityStats.empty")}</p>
                    )}
                </section>

                {/* ---- Spécialités (2e graphe : barres, couleur unique) ---- */}
                {specRows.length > 0 && (
                    <section className="aq2-card">
                        <h3 className="aq2-title">{t("communityStats.specialtiesTitle")}</h3>
                        <div className="aq2-gauge">
                            {specRows.map((r) => (
                                <button
                                    type="button"
                                    key={r.key}
                                    className="aq2-level aq2-specbar"
                                    onClick={() => onPickCert && onPickCert(r.key)}
                                    title={t("communityStats.seeDivers")}
                                >
                                    <span className="aq2-level-name">{t(`certifications.${r.key}`)}</span>
                                    <div className="aq2-level-track">
                                        <div className="aq2-level-fill" style={{ width: `${(r.count / specMax) * 100}%`, background: "#2a9d8f" }} />
                                    </div>
                                    <span className="aq2-level-count">{r.count}</span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                {/* ---- Les générations de plongeurs : pyramide des âges par sexe ---- */}
                {ageHasData && (
                    <section className="aq2-card">
                        <h3 className="aq2-title">{t("communityStats.ageTitle")}</h3>
                        {pyramidHasData ? (
                            <>
                                <div style={{ display: "flex", justifyContent: "center", gap: 18, marginBottom: 10, fontSize: 12, color: "#55707e" }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "#2a9d8f" }} />{t("communityStats.men")}</span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "#7b6cd0" }} />{t("communityStats.women")}</span>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    {pyramidRows.map((r) => (
                                        <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <span style={{ width: 46, fontSize: 12, fontWeight: 600, color: "#334455" }}>{r.label}</span>
                                            <span style={{ width: 18, textAlign: "right", fontSize: 11, color: "#8a97a3", fontVariantNumeric: "tabular-nums" }}>{r.male || ""}</span>
                                            <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                                                <div style={{ width: `${(r.male / pyramidMax) * 100}%`, minWidth: r.male ? 3 : 0, height: 16, background: "#2a9d8f", borderRadius: "5px 0 0 5px" }} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ width: `${(r.female / pyramidMax) * 100}%`, minWidth: r.female ? 3 : 0, height: 16, background: "#7b6cd0", borderRadius: "0 5px 5px 0" }} />
                                            </div>
                                            <span style={{ width: 18, textAlign: "left", fontSize: 11, color: "#8a97a3", fontVariantNumeric: "tabular-nums" }}>{r.female || ""}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="aq2-ages">
                                {ageRows.map((r, i) => (
                                    <div key={r.label} className="aq2-age">
                                        <span className="aq2-age-lbl">{r.label}</span>
                                        <div className="aq2-age-track">
                                            <div className="aq2-age-fill" style={{ width: `${(r.count / ageMax) * 100}%`, background: AGE_COLORS[i] }} />
                                        </div>
                                        <span className="aq2-age-count">{r.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {/* ---- Profil des plongées (loisir/déco + gaz) ---- */}
                {profileHasData && (
                    <section className="aq2-card">
                        <h3 className="aq2-title">{t("communityStats.profileTitle")}</h3>
                        {typeSegments.length > 0 && (
                            <div className="aq2-split-block">
                                <span className="aq2-split-lbl">{t("communityStats.diveTypeLabel")}</span>
                                <SplitBar segments={typeSegments} />
                            </div>
                        )}
                        {gasSegments.length > 0 && (
                            <div className="aq2-split-block">
                                <span className="aq2-split-lbl">{t("communityStats.gasLabel")}</span>
                                <SplitBar segments={gasSegments} />
                            </div>
                        )}
                        {visibilitySegments.length > 0 && (
                            <div className="aq2-split-block">
                                <span className="aq2-split-lbl">{t("communityStats.visibilityLabel")}</span>
                                <SplitBar segments={visibilitySegments} />
                            </div>
                        )}
                    </section>
                )}
            </div>

            <div className="aq2-sand" aria-hidden="true" />
        </div>
    );
}

// Barre proportionnelle segmentée + légende (%).
function SplitBar({ segments }) {
    const total = segments.reduce((s, x) => s + x.count, 0) || 1;
    return (
        <div className="aq2-split">
            <div className="aq2-split-bar">
                {segments.map((s, i) => (
                    <div
                        key={i}
                        className="aq2-split-seg"
                        style={{ width: `${(s.count / total) * 100}%`, background: s.color }}
                        title={`${s.label} — ${s.count}`}
                    />
                ))}
            </div>
            <div className="aq2-split-legend">
                {segments.map((s, i) => (
                    <span key={i} className="aq2-split-key">
                        <span className="aq2-split-dot" style={{ background: s.color }} />
                        {s.label} <b>{Math.round((s.count / total) * 100)}%</b> <span style={{ color: "#8a97a3", fontVariantNumeric: "tabular-nums" }}>({s.count})</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
